import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import {fetchData, fetchList} from "../../../../hook/axios.hook";

export const DEFAULT_TIME_UNIT_ID = 19;

const initialState = {
  currentDiscount: null,
  allDiscounts: [],
  contactDiscounts: [],
  brandDiscountFilters: {},

  //*** Sale rules */
  saleRulesFilters: {},
  allSaleRules: [],
  saleRule: null,

  loading: false,
  error: false,
};

export const createBrandDiscount = createAsyncThunk("brandDiscounts/createBrandDiscount", async data =>
  fetchData(`/brand/discount`, "post", data),
);

export const fetchBrandDiscounts = createAsyncThunk("brandDiscounts/fetchBrandDiscounts", async params => {
  const response = fetchList("/brand/discount", "get", {params});
  return response;
});

export const fetchBrandDiscountById = createAsyncThunk(
  "brandDiscounts/fetchBrandDiscountById",
  async brandDiscountId => {
    const response = fetchData(`/brand/discount/${brandDiscountId}`, "get");
    return response;
  },
);

export const updateBrandDiscount = createAsyncThunk(
  "brandDiscounts/updateBrandDiscount",
  async ({brandDiscountId, data}) => {
    const response = fetchData(`/brand/discount/${brandDiscountId}`, "patch", data);
    return response;
  },
);

export const deleteBrandDiscount = createAsyncThunk("brandDiscounts/deleteBrandDiscount", async brandDiscountId => {
  const response = fetchData(`/brand/discount/${brandDiscountId}`, "delete");
  return response;
});

export const fetchBrandDiscountFilters = createAsyncThunk("brandDiscounts/fetchBrandDiscountFilters", async params =>
  fetchData("/brand/discount/filters", "get", {params}),
);

//*** Sale rules */
export const fetchAllSaleRules = createAsyncThunk(
  "brandDiscounts/fetchAllSaleRules",
  async ({brandDiscountId, params}) => fetchList(`/brand/discount/${brandDiscountId}/saleRules`, "get", {params}),
);
export const fetchSaleRule = createAsyncThunk("brandDiscounts/fetchSaleRule", async saleRulesId =>
  fetchData(`/brand/discount/saleRules/${saleRulesId}`, "get"),
);
export const createSaleRule = createAsyncThunk("brandDiscounts/createSaleRule", async ({brandDiscountId, body}) =>
  fetchData(`/brand/discount/${brandDiscountId}/saleRules`, "post", body),
);
export const updateSaleRule = createAsyncThunk("brandDiscounts/updateSaleRule", async ({saleRulesId, body} = {}) =>
  fetchData(`/brand/discount/saleRules/${saleRulesId}`, "patch", body),
);
export const removeSaleRule = createAsyncThunk("brandDiscounts/removeSaleRule", async saleRulesId =>
  fetchData(`/brand/discount/saleRules/${saleRulesId}`, "delete"),
);

export const fetchSaleRuleFilters = createAsyncThunk(
  "brandDiscounts/fetchSaleRuleFilters",
  async ({brandDiscountId, params}) =>
    fetchData(`/brand/discount/${brandDiscountId}/saleRules/filters`, "get", {params}),
);

export const BRAND_DISCOUNTS_STORE_NAME = "finBlock";

const BrandDiscountSlice = createSlice({
  name: "brandDiscounts",
  initialState,
  reducers: {
    deselectCurrentBrandDiscount: state => {
      state.currentDiscount = null;
    },
    deselectCurrentSaleRule: state => {
      state.saleRule = null;
    },
    clearAllSaleRules: state => {
      state.allSaleRules = [];
    },
    clearContactDiscounts: state => {
      state.contactDiscounts = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(createBrandDiscount.fulfilled, (state, {payload}) => {
        state.currentDiscount = payload;
      })
      .addCase(fetchBrandDiscounts.fulfilled, (state, {payload}) => {
        if (!payload || !Array.isArray(payload.data)) return;
        state.contactDiscounts = payload.currentPage > 1 ? [...state.contactDiscounts, ...payload.data] : payload.data;
        state.allDiscounts = payload.currentPage > 1 ? [...state.allDiscounts, ...payload.data] : payload.data;
      })
      .addCase(fetchBrandDiscountById.fulfilled, (state, {payload}) => {
        state.currentDiscount = payload;
      })
      .addCase(updateBrandDiscount.fulfilled, (state, {payload}) => {
        state.currentDiscount = payload;
      })
      .addCase(deleteBrandDiscount.fulfilled, (state, action) => {
        state.allDiscounts = state.allDiscounts.filter(req => req.brandDiscountId !== action.meta.arg);
        state.currentDiscount = null;
      })
      .addCase(fetchBrandDiscountFilters.fulfilled, (state, {payload}) => {
        state.brandDiscountFilters = payload;
      })
      //*** Sale rules */
      .addCase(fetchAllSaleRules.fulfilled, (state, {payload}) => {
        state.allSaleRules = payload;
      })
      .addCase(fetchSaleRule.fulfilled, (state, {payload}) => {
        state.saleRule = payload;
      })
      .addCase(createSaleRule.fulfilled, (state, {payload}) => {
        state.saleRule = payload;
        state.allSaleRules = [...state.allSaleRules, payload];
      })
      .addCase(updateSaleRule.fulfilled, (state, {payload, meta}) => {
        state.saleRule = payload;
        state.allSaleRules = state.allSaleRules?.map(saleRule => {
          if (saleRule?.saleRulesId === meta.arg?.saleRulesId) {
            return payload;
          }
          return saleRule;
        });
      })
      .addCase(removeSaleRule.fulfilled, (state, {meta}) => {
        state.saleRule = null;
        state.allSaleRules = state.allSaleRules?.filter(({saleRulesId} = {}) => saleRulesId !== meta.arg);
      })
      .addCase(fetchSaleRuleFilters.fulfilled, (state, {payload}) => {
        state.saleRulesFilters = payload;
      })

      //fulfilled matcher
      .addMatcher(
        action => action.type.startsWith(BRAND_DISCOUNTS_STORE_NAME) && action.type.endsWith("/fulfilled"),
        state => handleFulfilled(state),
      )
      //pending matcher
      .addMatcher(
        action => action.type.startsWith(BRAND_DISCOUNTS_STORE_NAME) && action.type.endsWith("/pending"),
        state => handlePending(state),
      )
      //rejected matcher
      .addMatcher(
        action => action.type.startsWith(BRAND_DISCOUNTS_STORE_NAME) && action.type.endsWith("/rejected"),
        (state, {error}) => handleRejected(state, error),
      )
      //default
      .addDefaultCase(() => {});
  },
});

function handleFulfilled(state) {
  state.loading = false;
  state.error = false;
}

function handlePending(state) {
  state.loading = true;
  state.error = false;
}

function handleRejected(state, error) {
  state.loading = false;
  state.error = error;
}

//actions, reducer
const {actions, reducer} = BrandDiscountSlice;
export const {deselectCurrentBrandDiscount, deselectCurrentSaleRule, clearAllSaleRules, clearContactDiscounts} =
  actions;
export default reducer;

export const getCurrentBrandDiscount = state => state.brandDiscounts.currentDiscount;
export const getAllBrandDiscounts = state => state.brandDiscounts.allDiscounts;
export const selectBrandDiscountFilters = state => state.brandDiscounts.brandDiscountFilters;
export const selectBrandDiscountsLoading = state => state.brandDiscounts.loading;
//*** Sale rules */
export const selectSaleRulesFilters = state => state.brandDiscounts.saleRulesFilters;
export const selectAllSaleRules = state => state.brandDiscounts.allSaleRules;
export const selectCurrentSaleRule = state => state.brandDiscounts.saleRule;
export const selectContactDiscounts = state => state.brandDiscounts.contactDiscounts;
